import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createHash, randomBytes, randomInt } from "crypto";
import { normalizePhoneStr as normalizePhone } from "@/lib/phone";

/**
 * Identity & retargeting server functions.
 *
 * All writes go through the admin (service-role) client because lead tables
 * are server-managed. Anonymous IDs are caller-supplied (cookie) — we never
 * trust them for authorization, only for stitching behavior to a profile.
 */

const AnonIdSchema = z.string().min(8).max(64);
const PhoneSchema = z
  .string()
  .min(8)
  .max(20)
  .regex(/^[+0-9 \-()]+$/, "invalid phone");

// ---- trackEvent --------------------------------------------------------

export const trackEvent = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      anonymousId: string;
      eventId?: string;
      eventType: string;
      properties?: Record<string, unknown>;
      route?: string;
      brand?: string;
    }) =>
      z
        .object({
          anonymousId: AnonIdSchema,
          eventId: z.string().min(8).max(64).optional(),
          eventType: z.string().min(1).max(64),
          properties: z.record(z.string(), z.unknown()).optional(),
          route: z.string().max(255).optional(),
          brand: z.string().max(32).optional(),
        })
        .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();

    // Upsert profile by anonymous_id; bump last_seen / last_route.
    const last_product_handle =
      typeof data.properties?.handle === "string" ? (data.properties.handle as string) : undefined;
    const { data: profile, error: profileError } = await supabaseAdmin
      .from("lead_profiles")
      .upsert(
        {
          anonymous_id: data.anonymousId,
          last_seen: now,
          last_route: data.route,
          ...(last_product_handle ? { last_product_handle } : {}),
          ...(data.brand ? { brand: data.brand } : {}),
        },
        { onConflict: "anonymous_id" },
      )
      .select("id")
      .maybeSingle();
    if (profileError || !profile) return { ok: false, reason: "profile_write_failed" };

    const row = {
      anonymous_id: data.anonymousId,
      lead_profile_id: profile?.id ?? null,
      event_type: data.eventType,
      properties: (data.properties ?? {}) as never,
      route: data.route ?? null,
      brand: data.brand ?? null,
      ...(data.eventId ? { event_id: data.eventId } : {}),
    };
    // Old clients without eventId keep their original insert path. Deploy
    // the additive migration before this writer and regenerate DB types.
    const { error: eventError } = data.eventId
      ? await supabaseAdmin.from("lead_events").upsert(row as never, {
          onConflict: "anonymous_id,event_id", ignoreDuplicates: true,
        })
      : await supabaseAdmin.from("lead_events").insert(row as never);
    if (eventError) return { ok: false, reason: "event_write_failed" };

    return { ok: true };
  });

// ---- requestOtp --------------------------------------------------------
// Popup capture requires BOTH mobile (stored on the profile) AND email
// (used to receive the verification code). OTP is delivered by email.

export const requestOtp = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { anonymousId: string; phone: string; email: string; brand?: string }) =>
      z
        .object({
          anonymousId: AnonIdSchema,
          phone: PhoneSchema,
          email: z.string().email().max(255),
          brand: z.string().max(32).optional(),
        })
        .parse(data),
  )
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    if (!phone || phone.length < 10) {
      throw new Error("Invalid phone number");
    }
    const email = data.email.trim().toLowerCase();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Rate limit: max 3 requests/hour/email
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { count } = await supabaseAdmin
      .from("lead_otp_codes")
      .select("id", { count: "exact", head: true })
      .eq("email", email)
      .gte("created_at", oneHourAgo);
    if ((count ?? 0) >= 3) {
      throw new Error("Too many code requests. Try again in an hour.");
    }

    const code = String(randomInt(100000, 1000000));
    const salt = randomBytes(8).toString("hex");
    const code_hash = createHash("sha256").update(salt + code).digest("hex");
    const expires_at = new Date(Date.now() + 10 * 60 * 1000).toISOString();

    await supabaseAdmin.from("lead_otp_codes").insert({
      phone,
      email,
      code_hash,
      salt,
      expires_at,
    });

    try {
      const { sendTransactionalEmailServer } = await import("@/lib/email/send.server");
      const result = await sendTransactionalEmailServer({
        templateName: "lead-otp",
        recipientEmail: email,
        templateData: { code, offer: "your welcome offer" },
      });
      if (!result.ok && result.reason !== "suppressed") {
        console.error("[identity] OTP email enqueue failed", result.error);
      }
      // Dev fallback log so preview environments can read the code.
      if (process.env.NODE_ENV !== "production") {
        console.log(`[identity-otp] (dev) OTP for ${email}: ${code}`);
      }
    } catch (err) {
      console.error("[identity] OTP email send failed", err);
    }

    return { ok: true, email };
  });

// ---- verifyOtp ---------------------------------------------------------

export const verifyOtp = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      anonymousId: string;
      phone: string;
      email: string;
      code: string;
      consent: boolean;
      source: {
        popup_id: string;
        offer?: string;
        route?: string;
        brand?: string;
      };
    }) =>
      z
        .object({
          anonymousId: AnonIdSchema,
          phone: PhoneSchema,
          email: z.string().email().max(255),
          code: z.string().regex(/^\d{6}$/),
          consent: z.literal(true),
          source: z.object({
            popup_id: z.string().min(1).max(64),
            offer: z.string().max(255).optional(),
            route: z.string().max(255).optional(),
            brand: z.string().max(32).optional(),
          }),
        })
        .parse(data),
  )
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    const email = data.email.trim().toLowerCase();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: otps, error: otpErr } = await supabaseAdmin
      .from("lead_otp_codes")
      .select("id, code_hash, salt, expires_at, consumed_at, attempts")
      .eq("email", email)
      .is("consumed_at", null)
      .order("created_at", { ascending: false })
      .limit(1);
    if (otpErr) throw new Error("OTP lookup failed");
    const row = otps?.[0];
    if (!row) throw new Error("No active code. Request a new one.");
    if (new Date(row.expires_at).getTime() < Date.now()) {
      throw new Error("Code expired. Request a new one.");
    }
    if ((row.attempts ?? 0) >= 5) {
      throw new Error("Too many attempts. Request a new code.");
    }

    const hash = createHash("sha256").update(row.salt + data.code).digest("hex");
    if (hash !== row.code_hash) {
      await supabaseAdmin
        .from("lead_otp_codes")
        .update({ attempts: (row.attempts ?? 0) + 1 })
        .eq("id", row.id);
      throw new Error("Incorrect code");
    }

    await supabaseAdmin
      .from("lead_otp_codes")
      .update({ consumed_at: new Date().toISOString() })
      .eq("id", row.id);

    const now = new Date().toISOString();
    const { data: profile, error: upErr } = await supabaseAdmin
      .from("lead_profiles")
      .upsert(
        {
          anonymous_id: data.anonymousId,
          phone,
          email,
          email_verified_at: now,
          consent_whatsapp: true,
          consent_source: { ...data.source, consented_at: now, verified_via: "email" } as never,
          identified: true,
          brand: data.source.brand ?? null,
          last_seen: now,
        },
        { onConflict: "anonymous_id" },
      )
      .select("id")
      .single();
    if (upErr || !profile) {
      console.error("[identity] profile upsert failed", upErr);
      throw new Error("Could not save profile");
    }

    await supabaseAdmin
      .from("lead_events")
      .update({ lead_profile_id: profile.id })
      .eq("anonymous_id", data.anonymousId)
      .is("lead_profile_id", null);

    await supabaseAdmin.from("lead_popup_dismissals").insert({
      anonymous_id: data.anonymousId,
      popup_id: data.source.popup_id,
      action: "submitted",
    });

    // Guest-order stitching: many customers check out via Razorpay without
    // signing up, so their prior orders live under their phone number.
    // Match by normalized phone, attach the customer, and record the count.
    let matchedOrderCount = 0;
    try {
      const { data: existingCustomer } = await supabaseAdmin
        .from("customers")
        .select("id, email, name")
        .eq("phone", phone)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      const { count: phoneOrderCount } = await supabaseAdmin
        .from("orders")
        .select("id", { count: "exact", head: true })
        .eq("phone", phone);
      matchedOrderCount = phoneOrderCount ?? 0;

      let customerId = existingCustomer?.id ?? null;

      // Backfill email on the existing customer record if we just learned it.
      if (existingCustomer && !existingCustomer.email) {
        await supabaseAdmin
          .from("customers")
          .update({ email })
          .eq("id", existingCustomer.id);
      }

      // Stitch any historical orders that carry this phone but no customer_id.
      if (customerId) {
        await supabaseAdmin
          .from("orders")
          .update({ customer_id: customerId })
          .eq("phone", phone)
          .is("customer_id", null);
      }

      await supabaseAdmin
        .from("lead_profiles")
        .update({
          customer_id: customerId,
          matched_order_count: matchedOrderCount,
          matched_at: new Date().toISOString(),
        })
        .eq("id", profile.id);
    } catch (stitchErr) {
      console.error("[identity] guest order stitching failed", stitchErr);
    }

    // Issue this signup's personal one-time coupon (10% off, prepaid only).
    let coupon: Awaited<
      ReturnType<typeof import("@/lib/coupons.server").issueCouponForLead>
    > = null;
    try {
      const { issueCouponForLead } = await import("@/lib/coupons.server");
      coupon = await issueCouponForLead(supabaseAdmin, {
        leadProfileId: profile.id,
        customerId: null,
        phone,
        source: `popup:${data.source.popup_id}`,
      });
    } catch (couponErr) {
      console.error("[identity] coupon issue failed", couponErr);
    }

    return { ok: true, profileId: profile.id, matchedOrderCount, coupon };
  });


// ---- getMyLeadProfile --------------------------------------------------

export const getMyLeadProfile = createServerFn({ method: "POST" })
  .inputValidator((data: { anonymousId: string }) =>
    z.object({ anonymousId: AnonIdSchema }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: profile } = await supabaseAdmin
      .from("lead_profiles")
      // Anonymous IDs are guessable client-side identifiers, so this public
      // lookup never returns contact details (phone/email) or the linked
      // customer id — only what the site needs to personalise the page.
      .select("id, identified, last_product_handle, last_route, brand, matched_order_count")
      .eq("anonymous_id", data.anonymousId)
      .maybeSingle();
    if (!profile) return { identified: false } as const;
    return {
      identified: !!profile.identified,
      lastProductHandle: profile.last_product_handle,
      lastRoute: profile.last_route,
      brand: profile.brand,
      matchedOrderCount: profile.matched_order_count ?? 0,
    } as const;
  });

// ---- recordDismissal ---------------------------------------------------

export const recordDismissal = createServerFn({ method: "POST" })
  .inputValidator((data: { anonymousId: string; popupId: string }) =>
    z
      .object({
        anonymousId: AnonIdSchema,
        popupId: z.string().min(1).max(64),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("lead_popup_dismissals").insert({
      anonymous_id: data.anonymousId,
      popup_id: data.popupId,
      action: "dismissed",
    });
    return { ok: true };
  });
